/**
 * Centralized query cache configuration for React Query
 * Optimizes read-heavy data by reducing redundant API calls
 */

// Cache durations in milliseconds
export const CACHE_DURATIONS = {
  // User-specific data (changes occasionally)
  subscriptions: 60 * 1000,        // 1 minute - subscription status
  profile: 5 * 60 * 1000,          // 5 minutes - user profile data
  userRoles: 5 * 60 * 1000,        // 5 minutes - user roles
  
  // Static/semi-static data (rarely changes)
  badges: 60 * 60 * 1000,          // 1 hour - badge definitions
  curriculum: 60 * 60 * 1000,      // 1 hour - curriculum structure
  resources: 30 * 60 * 1000,       // 30 minutes - resource library
  
  // Dynamic data (changes frequently)
  notifications: 30 * 1000,        // 30 seconds - notifications
  messages: 15 * 1000,             // 15 seconds - message inbox
  castings: 2 * 60 * 1000,         // 2 minutes - casting listings
  
  // Real-time data (polling fallback)
  communityPosts: 30 * 1000,       // 30 seconds - community feed
  castingSubmissions: 60 * 1000,   // 1 minute - submissions status
} as const;

// Query keys for consistent cache management
export const QUERY_KEYS = {
  subscriptions: (userId: string, profileId?: string) => 
    ['subscriptions', userId, profileId || userId] as const,
  profile: (userId: string) => ['profile', userId] as const,
  userRoles: (userId: string) => ['userRoles', userId] as const,
  badges: () => ['badges'] as const,
  curriculum: () => ['curriculum'] as const,
  curriculumBox: (boxId: string) => ['curriculum', 'box', boxId] as const,
  notifications: (userId: string) => ['notifications', userId] as const,
  messages: (userId: string) => ['messages', userId] as const,
  castings: (filters?: Record<string, unknown>) => ['castings', filters] as const,
  casting: (id: string) => ['casting', id] as const,
  communityPosts: (groupId?: string) => ['communityPosts', groupId] as const,
  resources: (category?: string) => ['resources', category] as const,
} as const;

/**
 * In-memory cache for subscription data
 * Used by useSubscription hook for immediate access
 */
interface CacheEntry<T> {
  data: T;
  timestamp: number;
  expiresAt: number;
}

class SubscriptionCache {
  private cache: Map<string, CacheEntry<unknown>> = new Map();

  private getKey(userId: string, profileId?: string): string {
    return `${userId}-${profileId || userId}`;
  }

  get<T>(userId: string, profileId?: string): T | null {
    const key = this.getKey(userId, profileId);
    const entry = this.cache.get(key);
    
    if (!entry) return null;
    
    if (Date.now() > entry.expiresAt) {
      this.cache.delete(key);
      return null;
    }
    
    return entry.data as T;
  }

  set<T>(userId: string, profileId: string | undefined, data: T, ttl: number = CACHE_DURATIONS.subscriptions): void {
    const key = this.getKey(userId, profileId);
    const now = Date.now();
    
    this.cache.set(key, {
      data,
      timestamp: now,
      expiresAt: now + ttl,
    });
  }

  invalidate(userId: string, profileId?: string): void {
    const key = this.getKey(userId, profileId);
    this.cache.delete(key);
  }

  invalidateAll(): void {
    this.cache.clear();
  }

  getAge(userId: string, profileId?: string): number | null {
    const key = this.getKey(userId, profileId);
    const entry = this.cache.get(key);
    
    if (!entry) return null;
    
    return Date.now() - entry.timestamp;
  }
}

// Singleton instance
export const subscriptionCache = new SubscriptionCache();

/**
 * Profile data cache for quick lookups
 */
class ProfileCache {
  private cache: Map<string, CacheEntry<unknown>> = new Map();

  get<T>(userId: string): T | null {
    const entry = this.cache.get(userId);
    
    if (!entry) return null;
    
    if (Date.now() > entry.expiresAt) {
      this.cache.delete(userId);
      return null;
    }
    
    return entry.data as T;
  }

  set<T>(userId: string, data: T, ttl: number = CACHE_DURATIONS.profile): void {
    const now = Date.now();
    
    this.cache.set(userId, {
      data,
      timestamp: now,
      expiresAt: now + ttl,
    });
  }

  invalidate(userId: string): void {
    this.cache.delete(userId);
  }

  invalidateAll(): void {
    this.cache.clear();
  }
}

// Singleton instance
export const profileCache = new ProfileCache();

/**
 * Static data cache (badges, curriculum, etc.)
 * These rarely change and can be cached aggressively
 */
class StaticDataCache {
  private cache: Map<string, CacheEntry<unknown>> = new Map();

  get<T>(key: string): T | null {
    const entry = this.cache.get(key);
    
    if (!entry) return null;
    
    if (Date.now() > entry.expiresAt) {
      this.cache.delete(key);
      return null;
    }
    
    return entry.data as T;
  }

  set<T>(key: string, data: T, ttl: number): void {
    const now = Date.now();
    
    this.cache.set(key, {
      data,
      timestamp: now,
      expiresAt: now + ttl,
    });
  }

  invalidate(key: string): void {
    this.cache.delete(key);
  }

  invalidateAll(): void {
    this.cache.clear();
  }
}

// Singleton instance
export const staticDataCache = new StaticDataCache();
